import {createApiCall} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions} from '../types';
import type {
    OAuthTokenResource,
    UserAuthorizationTokenResource,
    RecipientAuthorizationTokenResource,
    MemberAuthorizationTokenResource,
    StoreRecipientAuthorizationTokenRequest,
    StoreUserAuthorizationTokenRequest,
    ValidateSessionStateRequest,
    SsoAuthRequest,
    SsoCheckParams,
    RefreshTokenRequest,
    SigningUrlResponse,
    PaginatedResponse
} from './api.d';

/** GET /validate/api-key - Validate API key and return OAuth token */
export const validateApiKey = (userId: string, apiSecret: string, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        'validate/api-key'
    )({...options, params: {user_id: userId, api_secret: apiSecret}});

/** GET /authorize/{secret} - Authorizes user/recipient/member; generates OAuth tokens via Keycloak token exchange */
export const authorizeWithSecret = (secret: string, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `authorize/${secret}`
    )({...options, skipAuth: true});

/** GET /delegations/{delegatedAccountUserId}/authorize - Authorizes a delegation for a delegated account user */
export const authorizeDelegation = (delegatedAccountUserId: string, accountId?: string, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `delegations/${delegatedAccountUserId}/authorize`
    )({
        ...options,
        headers: {
            ...options.headers,
            ...(accountId ? {'Session-Account-Id': accountId} : {})
        }
    });

/** GET /transactions/{transactionId}/recipients/{recipientId}/face-to-face - Authorizes a face-to-face transaction */
export const authorizeFaceToFace = (transactionId: string, recipientId: string, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/face-to-face`
    )(options);

/** POST /exchange-session/deauthorize - Deauthorizes a session */
export const deauthorizeExchangeSession = (options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        'exchange-session/deauthorize'
    )(options);

/** POST /authorize/legacy - Authorize a recipient using a legacy loginToken */
export const authorizeLegacy = (loginToken: string, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        'authorize/legacy'
    )({...options, data: {loginToken}, useS2SAuth: true});

/** POST /authorize/sso-auth - Exchange authorization code for OAuth tokens (SSO flow) */
export const authorizeSso = (data: SsoAuthRequest, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        'authorize/sso-auth'
    )({...options, data, useS2SAuth: true});

export const refreshSession = (data: ValidateSessionStateRequest, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        'session/refresh'
    )({...options, data, useS2SAuth: true});

/** GET /authorize/sso-check - Validate SSO authorization code against the authenticated user */
export const checkSsoAuthorization = (params: SsoCheckParams, options: ApiCallOptions = {}) =>
    createApiCall<void>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        'authorize/sso-check'
    )({...options, params});

/** POST /token/refresh - Refresh an access token using a refresh token */
export const refreshToken = (data: RefreshTokenRequest, options: ApiCallOptions = {}) =>
    createApiCall<OAuthTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        'token/refresh'
    )({...options, data});

/** GET /transactions/{transactionId}/recipients/{recipientId}/tokens - Retrieves a list of tokens for the recipient */
export const getRecipientTokens = (
    transactionId: string,
    recipientId: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<PaginatedResponse<RecipientAuthorizationTokenResource>>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/tokens`
    )(options);

/** POST /transactions/{transactionId}/recipients/{recipientId}/tokens - Creates a new token for a recipient */
export const createRecipientToken = (
    transactionId: string,
    recipientId: string,
    data: StoreRecipientAuthorizationTokenRequest,
    options: ApiCallOptions = {}
) =>
    createApiCall<RecipientAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/tokens`
    )({...options, data});

/** GET /transactions/{transactionId}/recipients/{recipientId}/tokens/{token} - Retrieve specific recipient token */
export const getRecipientToken = (
    transactionId: string,
    recipientId: string,
    token: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<RecipientAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/tokens/${token}`
    )(options);

/** DELETE /transactions/{transactionId}/recipients/{recipientId}/tokens/{token} - Delete specific recipient token */
export const deleteRecipientToken = (
    transactionId: string,
    recipientId: string,
    token: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<void>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'DELETE',
        `transactions/${transactionId}/recipients/${recipientId}/tokens/${token}`
    )(options);

/** GET /transactions/{transactionId}/recipients/{recipientId}/signing-url - Generates a signing URL for a recipient */
export const getRecipientSigningUrl = (
    transactionId: string,
    recipientId: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<SigningUrlResponse>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/signing-url`
    )(options);

/** GET /transactions/{transactionId}/recipients/{recipientId}/members/{memberId}/tokens - Retrieves a list of tokens for the member */
export const getMemberTokens = (
    transactionId: string,
    recipientId: string,
    memberId: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<PaginatedResponse<MemberAuthorizationTokenResource>>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/members/${memberId}/tokens`
    )(options);

/** POST /transactions/{transactionId}/recipients/{recipientId}/members/{memberId}/tokens - Creates a new token for a member */
export const createMemberToken = (
    transactionId: string,
    recipientId: string,
    memberId: string,
    data: StoreRecipientAuthorizationTokenRequest,
    options: ApiCallOptions = {}
) =>
    createApiCall<MemberAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/members/${memberId}/tokens`
    )({...options, data});

/** GET /transactions/{transactionId}/recipients/{recipientId}/members/{memberId}/tokens/{token} - Retrieve specific member token */
export const getMemberToken = (
    transactionId: string,
    recipientId: string,
    memberId: string,
    token: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<MemberAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/members/${memberId}/tokens/${token}`
    )(options);

/** DELETE /transactions/{transactionId}/recipients/{recipientId}/members/{memberId}/tokens/{token} - Delete specific member token */
export const deleteMemberToken = (
    transactionId: string,
    recipientId: string,
    memberId: string,
    token: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<void>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'DELETE',
        `transactions/${transactionId}/recipients/${recipientId}/members/${memberId}/tokens/${token}`
    )(options);

/** GET /transactions/{transactionId}/recipients/{recipientId}/members/{memberId}/signing-url - Generates a signing URL for a member */
export const getMemberSigningUrl = (
    transactionId: string,
    recipientId: string,
    memberId: string,
    options: ApiCallOptions = {}
) =>
    createApiCall<SigningUrlResponse>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}/members/${memberId}/signing-url`
    )(options);

/** GET /users/{userId}/tokens - Retrieves a list of tokens for the user */
export const getUserTokens = (userId: string, options: ApiCallOptions = {}) =>
    createApiCall<PaginatedResponse<UserAuthorizationTokenResource>>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `users/${userId}/tokens`
    )(options);

/** POST /users/{userId}/tokens - Creates a new token for a user */
export const createUserToken = (
    userId: string,
    data: StoreUserAuthorizationTokenRequest,
    options: ApiCallOptions = {}
) =>
    createApiCall<UserAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'POST',
        `users/${userId}/tokens`
    )({...options, data});

/** GET /users/{userId}/tokens/{token} - Retrieve specific user token */
export const getUserToken = (userId: string, token: string, options: ApiCallOptions = {}) =>
    createApiCall<UserAuthorizationTokenResource>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'GET',
        `users/${userId}/tokens/${token}`
    )(options);

/** DELETE /users/{userId}/tokens/{token} - Deletes a specific token for a user */
export const deleteUserToken = (userId: string, token: string, options: ApiCallOptions = {}) =>
    createApiCall<void>(
        SERVICE_NAMES.AUTHORIZATION_TOKEN,
        'DELETE',
        `users/${userId}/tokens/${token}`
    )(options);

/** @deprecated Use authorizeLegacy instead */
export const authorizeWithLegacyToken = authorizeLegacy;

/** @deprecated Use authorizeDelegation instead */
export const startDelegation = authorizeDelegation;

/** @deprecated Use authorizeFaceToFace instead */
export const faceToFaceAuth = authorizeFaceToFace;

export const authorizationTokenApi = {
    validateApiKey,
    authorizeWithSecret,
    authorizeDelegation,
    authorizeFaceToFace,
    deauthorizeExchangeSession,
    authorizeLegacy,
    authorizeSso,
    checkSsoAuthorization,
    refreshSession,
    refreshToken,
    getRecipientTokens,
    createRecipientToken,
    getRecipientToken,
    deleteRecipientToken,
    getRecipientSigningUrl,
    getMemberTokens,
    createMemberToken,
    getMemberToken,
    deleteMemberToken,
    getMemberSigningUrl,
    getUserTokens,
    createUserToken,
    getUserToken,
    deleteUserToken,
    authorizeWithLegacyToken,
    startDelegation,
    faceToFaceAuth
};
